import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "primeicons/primeicons.css";
import { useEffect, useState } from "react";
import httpService from "../../services/httpService";
import { ApiEndpoints, Constants } from "../../constants/appConstants";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Dialog } from "primereact/dialog";
import { useAppContext } from "../../Context";
import { FilterMatchMode, FilterOperator, FilterService } from "primereact/api";
import { getClientLocalDate, formatClientLocalDate } from "../../services/dateHelper";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import MultipartFileUploadComponent from "../FileUpload/MultpartFileUploadComponent";
import { Calendar } from "primereact/calendar";
import { useAuthContext } from "../../Context/AuthContext";
import { useNavigate } from "react-router";

FilterService.register('dateIs', (value: any, filter: any) => {
  if (filter === undefined || filter === null) {
    return true;
  }
  if (value === undefined || value === null) {
    return false;
  }
  const normalizedValue = value instanceof Date ? value : getClientLocalDate(value);
  const normalizedFilter = filter instanceof Date ? filter : getClientLocalDate(filter);
    
  if (!normalizedValue || !normalizedFilter) {
    return false;
  }
  return normalizedValue.getTime() === normalizedFilter.getTime();
});

FilterService.register('dateIsNot', (value: any, filter: any) => {
  if (filter === undefined || filter === null) {
    return true;
  }
  if (value === undefined || value === null) {
    return true;
  }
  const normalizedValue = value instanceof Date ? value : getClientLocalDate(value);
  const normalizedFilter = filter instanceof Date ? filter : getClientLocalDate(filter);
    
  if (!normalizedValue || !normalizedFilter) {
    return true;
  }
  return normalizedValue.getTime() !== normalizedFilter.getTime();
});

export const ProviderFileInfoComponent = (props:any) => {
  // Custom filter options for hasErrors
  const hasErrorsFilterOptions = [
    { label: 'All', value: '' },
    { label: 'Passed', value: 'false' },
    { label: 'Failed', value: 'true' }
  ];

  // Custom filter element for hasErrors
  const hasErrorsFilterElement = (options: any) => (
    <select
      value={options.value === undefined ? '' : String(options.value)}
      onChange={e => {
        const val = e.target.value;
        let filterVal = null;
        if (val === 'true') filterVal = true;
        else if (val === 'false') filterVal = false;
        else filterVal = null;
        options.filterCallback(filterVal, options.index);
      }}
      className="p-inputtext p-component"
      style={{ width: '100%' }}
    >
      {hasErrorsFilterOptions.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
  // Converts bytes to human-readable format
  const readableFileSize = (bytes: number): string => {
    if (bytes === 0 || bytes == null) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fileSizeTemplate = (rowData: any) => {
    return readableFileSize(rowData.fileSize);
  };
  const {isAdministrator} = useAuthContext();
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [filteredData, setFilteredData] = useState(data);
  const [isLoading, setLoading] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [rowData,setRowData] = useState(null);
  const { reviewYear: contextReviewYear } = useAppContext();
  const reviewYear = props.reviewYear ?? contextReviewYear;
  const [filters, setFilters] = useState<any>(null);
  const navigate = useNavigate();
  const globalSearchKeys = ['formattedCreatedAt', 'fileName', 'createdBy', 'documentType', 'validationStatusText'];

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  // api call onload
  useEffect(() => {
    if (!reviewYear) return;
    setLoading(true);
    if (props.showBulkData) {
      httpService
        .get(`${ApiEndpoints.GetSupportingBulkUploadData}`)
        .then((resp: any) => {
          const updatedData = resp?.data.map((item:any)=>({
                ...item,
                createdAt:getDateOnly(item.createdAt),
                formattedCreatedAt: item.createdAt ? new Date(item.createdAt + "Z").toLocaleDateString() : '',
                validationStatusText: item.hasErrors === true ? 'Failed' : 'Passed',
            }))
          setData(updatedData);
          setFilterData(updatedData)
        })
        .catch((error: any) => {
          console.log("error", error);
        })
        .finally(() => {
          setLoading(false);
        });
        setFilters({
    global: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    documentType: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    createdBy: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    fileName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    hasErrors: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
  })
    } else {
      httpService
        .get(`${ApiEndpoints.GetAllProviderFileInfo}/${reviewYear}`)
        .then((response: any) => {
          setData(response?.data);
          setFilteredData(response?.data);
        })
        .catch((error: any) => {
          if (error.status == 403 || error.status == 401) {
            navigate(`/unauthorized`);
          }
          console.log("Error: ", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [reviewYear,props.isFileUploaded,isFileUploaded]);

  const hasErrorsTemplate = (rowData: any) => {
    return rowData.hasErrors == true ? (
      <Badge value="Yes" severity="danger"></Badge>
    ) : (
      <Badge value="No" severity="success"></Badge>
    );
  };

  const errorTemplate = (rowData: any) =>{
    return <>
      <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start'}}>
    <span style={{marginRight:8,textWrap:'pretty'}}>{rowData.hasErrors?'Failed':'Passed'}</span>
    {rowData.hasErrors && <a style={{ cursor: "pointer" ,color:'blue'}} onClick={()=> downloadErrorReport(rowData)}>Download Error Report</a>}
  </div>
    </>
  }

  const getDateOnly = (date: string) => {
    return getClientLocalDate(date);
  };

  const downloadErrorReport = (rowData: any) => {
    var fileId = rowData.fileId;
    httpService
      .get(`${ApiEndpoints.DownloadErrorReport}/${fileId}`)
      .then((response: any) => {
        let fileName = rowData.fileName
          .replace(".xlsx", "_ErrorReport.csv")
          .replace(".xls", "_ErrorReport.csv");
        if (!fileName.endsWith(".csv")) {
          fileName = fileName + "_ErrorReport.csv";
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        link.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error: any) => {
        console.log("Error: ", error);
        setShowConfirmDialog(true);
      })
      .finally(() => {});
  };

  const downloadErrorReportTemplate = (rowData: any) => {
    return rowData.hasErrors == true ? (
      <Button
        label="View/Download"
        icon="pi pi-download"
        className="p-button-secondary"
        onClick={() => downloadErrorReport(rowData)}
      />
    ) : (
      <Button
        label="No Errors"
        icon="pi pi-download"
        className="p-button-secondary"
        disabled
      />
    );
  };

  const downloadDataFile = (rowData: any) => {
    httpService
      .getBlob(`${ApiEndpoints.DownloadProviderDataFile}/${rowData.fileGuid}`)
      .then(async (response: any) => {
        if (response) {
          if(response.data?.url){
          let url = response.data.url;
          const validatedUrl = new URL(url);
          if (
            validatedUrl.protocol === "http:" ||
            validatedUrl.protocol === "https:"
          ) {
            const anchor = document.createElement("a");
            anchor.href = validatedUrl.toString();
            //anchor.download = fileName;
            anchor.click();
          }
        }else{
          let fileName = rowData.fileName.replace(".xlsx", ".json").replace(".xls", ".json");
          if (!fileName.endsWith(".json")) {
            fileName = fileName + ".json";
          }
          const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        link.click();
        window.URL.revokeObjectURL(url);
        }
        }
      })
      .catch((error: any) => {
        console.log("Error: ", error);
        setShowConfirmDialog(true);
      })
      .finally(() => {});
  };

  const downloadDataFile_New = (rowData: any) => {
    httpService
      .get(`${ApiEndpoints.DownloadProviderDataFile}/${rowData.fileGuid}`)
      .then(async (response: any) => {
        if (response) {
          if(response.data?.url){
          let url = response.data.url;
          const validatedUrl = new URL(url);
          if (
            validatedUrl.protocol === "http:" ||
            validatedUrl.protocol === "https:"
          ) {
            const anchor = document.createElement("a");
            anchor.href = validatedUrl.toString();
            anchor.target = "_blank";
            //anchor.download = fileName;
            anchor.click();
          }
        }else{
          throw new Error("File Not Found!")
        }
        }
      })
      .catch((error: any) => {
        console.log("Error: ", error);
        setShowConfirmDialog(true);
      })
      .finally(() => {});
  };
  
  const downloadDataFileTemplate = (rowData: any) => {
    return (
      <a
        style={{
          textDecoration: "underline",
          color: "blue",
          cursor: "pointer",
        }}
        onClick={() => downloadDataFile(rowData)}
      >
        {rowData.fileName}
      </a>
    );
  };
  
  const fileNameTemplate = (rowData: any) => (
  <div style={{display:'flex', flexDirection:'row', justifyContent:'flex-start'}}>
    <span style={{marginRight:8,textWrap:'pretty'}}>{rowData.fileName}</span>
    <a style={{ cursor: "pointer", marginRight: 8,color:'blue' }} onClick={()=>downloadDataFile_New(rowData)}>View</a>
    {!props.isDucLocked && isAdministrator() && <a style={{ cursor: "pointer" ,color:'blue'}} onClick={()=>{setRowData(rowData);setShowDialog(true);setIsFileUploaded(false)}}>Replace</a>}
  </div>
);

const fileCreationDateTemplate = (rowData:any) => {
  if (rowData.createdAt) {
      return formatClientLocalDate(rowData.createdAt);
  };
};
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const hideDialog = () => {
    setShowConfirmDialog(false);
  };

  // const onGlobalFilterChange = (e: any) => {
  //   const value = e.target.value;
  //   let _filters: any = { ...filters };

  //   _filters['global'].value = value;

  //   setFilters(_filters);
  //   setGlobalFilterValue(value);
  // };

      const handleSearch = (e: any) => {
    let value = "";
    if (Array.isArray(e) && typeof e.join("") == "string") {
      value = e.join("");
    } else value = e.target.value;
    let filtered = data.filter((item: any) =>
      globalSearchKeys.some((key) => {
        const fieldValue = item[key] !== undefined && item[key] !== null ? String(item[key]).toLowerCase() : '';
        return fieldValue.includes(value?.toLowerCase());
      })
    );
    if (value == globalFilterValue) {
      value = "";
      filtered = data;
    }

    setGlobalFilterValue(value);
    setFilterData(filtered);
  };

      const dateFilterTemplate = (options: any) => {
        return (
          <Calendar
            value={options.value}
              onChange={(e) => {
      const selectedDate = e.value;
      if (selectedDate) {
          const clientDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0, 0);
          options.filterCallback(clientDate, options.index);
      } else {
          options.filterCallback(null, options.index);
      }
  }}
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            mask="99/99/9999"
          />
        );
      };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-start">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText value={globalFilterValue} onChange={handleSearch} placeholder="Search File" />
        </IconField>
      </div>
    );
  };

  const header = renderHeader();
  return (
    <>
      {/* <MegaMenu model={navigationItems} breakpoint='960px' /> */}
      <div className="card">
        <h3>Uploaded Files</h3>
        <div>
          {props.showBulkData ? (
            <DataTable
              value={filterData}
              paginator
              header={header}
              rows={5}
              rowsPerPageOptions={[5, 10, 25, 50]}
              loading={isLoading}
              emptyMessage="No provider Files found."
              filters={filters}
              globalFilterFields={['createdAt', 'fileName', 'createdBy', 'documentType','hasErrors']}
            >
              <Column field="createdAt" sortable filter filterPlaceholder="YYYY-MM-DD" dataType="date" header="DATE" body={fileCreationDateTemplate} filterElement={dateFilterTemplate}/>
              <Column field="fileName" sortable filter header="FILE NAME" body={fileNameTemplate} />
              <Column field="documentType" header="FILE TYPE" sortable filter />
              <Column field="hasErrors" header="VALIDATION STATUS" sortable filter filterElement={hasErrorsFilterElement} body={errorTemplate} showFilterMatchModes={false} filterMatchMode="equals" />
              <Column field="createdBy" sortable filter header="UPLOADED BY" />
              <Column field="fileSize" sortable header="SIZE" body={fileSizeTemplate} />
            </DataTable>
          ) : (
            <DataTable
              value={filteredData}
              paginator
              rows={5}
              rowsPerPageOptions={[5, 10, 25, 50]}
              loading={isLoading}
              emptyMessage="No provider Files found."
            >
              <Column
                field="fileName"
                header="File Name"
                body={downloadDataFileTemplate}
                sortable
                filter
                filterPlaceholder="Filter by File Name"
                style={{ minWidth: "14rem" }}
              />
              <Column
                field="version"
                header="Version"
                sortable
                filter
                filterPlaceholder="Filter by Version"
                style={{ minWidth: "14rem" }}
              />
              <Column
                field="createdAt"
                header="Date Uploaded"
                sortable
                filter
                filterPlaceholder="Filter by Date Uploaded"
                style={{ minWidth: "14rem" }}
              />
              <Column
                field="hasErrors"
                header="Has Errors"
                body={hasErrorsTemplate}
                sortable
                filter
                filterPlaceholder="Filter by Has Errors"
                style={{ minWidth: "14rem" }}
              />
              <Column
                field="viewErrorReport"
                header="View Error Report"
                body={downloadErrorReportTemplate}
                style={{ minWidth: "14rem" }}
                align={"center"}
                alignHeader={"center"}
              />
            </DataTable>
          )}

          <Dialog
            header="File not found!"
            visible={showConfirmDialog}
            onHide={hideDialog}
            footer={
              <div style={{ display: "flex", justifyContent: "end" }}>
                <Button
                  label="Close"
                  icon="pi pi-times"
                  className="p-button-secondary"
                  onClick={hideDialog}
                  autoFocus
                />
              </div>
            }
          >
            <div>
              <p>{Constants.FILE_NOT_FOUND_ERROR}</p>
            </div>
          </Dialog>

                <Dialog visible={showDialog} onHide={() => {setShowDialog(false);setIsFileUploaded(true);}} headerStyle={{backgroundColor:"white"}}>
        <MultipartFileUploadComponent className="p-mb-3" isBulkUpload={true} isReplace={true} rowData={rowData} allowedExtension=".csv,.xls,.xlsx"></MultipartFileUploadComponent>
      </Dialog>
        </div>
      </div>
    </>
  );
};
